'use client'

import { useEffect, useState } from 'react'
import { Smartphone, X, Share } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'susy-pwa-install-dismissed'
const DISMISS_DAYS = 14

export function PwaRegister() {
  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosBanner, setShowIosBanner] = useState(false)
  const [showAndroidBanner, setShowAndroidBanner] = useState(false)

  useEffect(() => {
    // Service Worker registration
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }

    // Skip prompts se già installata o già dismissed
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error iOS-only flag
      window.navigator.standalone === true
    if (isStandalone) return

    const dismissed = localStorage.getItem(DISMISS_KEY)
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400 * 1000) return

    // Android / Chrome → beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault()
      setInstallEvt(e as BeforeInstallPromptEvent)
      setShowAndroidBanner(true)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // iOS Safari → istruzioni manuali (non c'è prompt nativo)
    const ua = navigator.userAgent.toLowerCase()
    const isIos = /iphone|ipad|ipod/.test(ua) && !ua.includes('crios') && !ua.includes('fxios')
    if (isIos) {
      // Mostra banner dopo 4 secondi (non subito, per non disturbare)
      const t = setTimeout(() => setShowIosBanner(true), 4000)
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handler) }
    }

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setShowIosBanner(false)
    setShowAndroidBanner(false)
  }

  async function installNow() {
    if (!installEvt) return
    await installEvt.prompt()
    const res = await installEvt.userChoice
    if (res.outcome === 'accepted') dismiss()
    setInstallEvt(null)
    setShowAndroidBanner(false)
  }

  if (!showIosBanner && !showAndroidBanner) return null

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl p-4 shadow-lg"
      style={{ backgroundColor: 'var(--susy-verde-bosco)', color: 'var(--susy-beige-fieno)' }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--susy-beige-fieno)' }}>
          <Smartphone className="w-5 h-5" style={{ color: 'var(--susy-verde-bosco)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base mb-0.5">Installa Susy® Hub</div>
          {showAndroidBanner ? (
            <p className="text-xs opacity-80 leading-relaxed">
              Aggiungi l&apos;hub alla schermata home per accesso rapido come app.
            </p>
          ) : (
            <p className="text-xs opacity-80 leading-relaxed inline-flex items-center gap-1 flex-wrap">
              Tocca <Share className="w-3 h-3 inline" /> in basso, poi <strong>&quot;Aggiungi alla schermata Home&quot;</strong>
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {showAndroidBanner && (
              <button
                onClick={installNow}
                className="px-3 h-9 rounded-lg text-xs font-medium cursor-pointer"
                style={{ backgroundColor: 'var(--susy-beige-fieno)', color: 'var(--susy-verde-bosco)' }}
              >
                Installa
              </button>
            )}
            <button onClick={dismiss} className="px-3 h-9 rounded-lg text-xs cursor-pointer hover:bg-white/10">
              Più tardi
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="p-1 rounded hover:bg-white/10 cursor-pointer shrink-0" aria-label="Chiudi">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
